import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import type { ProductionBatch, ProductionSummary } from '../../Services/HCP/flows/production-batch.interface'
import { orderProductionOverview, productionBatchLabel } from '../../utils/order-production-batches'

const FLOW_LABELS: Record<string, string> = {
  AWAITING_ART: 'Arte Final', SHEET: 'Cartela', PRODUCTION: 'Produção',
  MACHINE_PRODUCTION: 'Produção de Máquinas', LOGISTICS: 'Logística',
}
const flowLabel = (flow?: string | null) => flow ? FLOW_LABELS[flow] ?? flow : 'Sem posição ativa'
const quantity = (value: number) => value.toLocaleString('pt-BR')
const dateLabel = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

interface ProductionBatchesProps {
  batches?: ProductionBatch[] | null
  summary?: ProductionSummary | null
  commercialQuantity?: number | null
}

export function OrderProductionSummary({ batches = [], summary, commercialQuantity, compact = false }: ProductionBatchesProps & { compact?: boolean }) {
  const overview = orderProductionOverview(batches ?? [], summary, commercialQuantity)
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="body2" fontWeight={600}>
      {quantity(overview.commercialQuantity)} un comerciais · {overview.commercialBatchesCount} {overview.commercialBatchesCount === 1 ? 'lote comercial' : 'lotes comerciais'}
    </Typography>
    {overview.reworkBatchesCount > 0 && <Typography variant="caption" color="warning.main">
      Retrabalho adicional: {quantity(overview.reworkQuantity)} un · {overview.reworkBatchesCount} {overview.reworkBatchesCount === 1 ? 'lote' : 'lotes'}
    </Typography>}
    {overview.hasMultiplePositions && <Typography variant="caption" fontWeight={700}>Múltiplas posições</Typography>}
    {(!compact || !overview.hasMultiplePositions) && <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {overview.positions.map((position, index) => <Chip key={index} size="small" variant="outlined"
        color={position.finished ? 'success' : 'primary'}
        label={`${position.finished ? 'Finalizado' : [position.displayCode, flowLabel(position.flow)].filter(Boolean).join(' · ')} · ${quantity(position.quantity)} un`}
        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.25 } }} />)}
    </Box>}
  </Box>
}

export function OrderProductionBatches(props: ProductionBatchesProps) {
  const batches = props.batches ?? []
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <OrderProductionSummary {...props} />
    {!batches.length ? <Typography variant="body2" color="text.secondary">Detalhamento dos lotes indisponível.</Typography>
      : <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" aria-label="Lotes de produção do item" sx={{ minWidth: 720, '& td, & th': { verticalAlign: 'top', whiteSpace: 'normal', overflowWrap: 'anywhere' } }}>
          <TableHead><TableRow>
            <TableCell>Lote</TableCell><TableCell align="right">Qtd. inicial</TableCell><TableCell align="right">Saldo</TableCell>
            <TableCell>Posição HCP</TableCell><TableCell>Início</TableCell><TableCell>Prazo da etapa</TableCell><TableCell>Situação</TableCell>
          </TableRow></TableHead>
          <TableBody>{batches.map((batch) => <TableRow key={batch.id}>
            <TableCell>
              <Chip size="small" label={productionBatchLabel(batch)} color={batch.type === 'REWORK' ? 'warning' : batch.type === 'DIVISION' ? 'primary' : 'default'} variant="outlined" />
              {batch.type === 'REWORK' && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Produção adicional</Typography>}
              {batch.reworkReason && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Motivo: {batch.reworkReason}</Typography>}
              {batch.hcpPreServiceOrderId && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Vinculado a Pré-OS</Typography>}
            </TableCell>
            <TableCell align="right">{quantity(batch.initialQuantity)} un</TableCell>
            <TableCell align="right"><Typography variant="body2" fontWeight={700}>{quantity(batch.quantity)} un</Typography></TableCell>
            <TableCell>{batch.hcpFinished ? 'Finalizado' : <>
              <Typography variant="body2" fontWeight={600}>{[batch.hcpCurrentFlowDisplayCode, flowLabel(batch.hcpCurrentFlow)].filter(Boolean).join(' · ')}</Typography>
              {batch.hcpCurrentFlowStatusName && <Typography variant="caption" display="block">{batch.hcpCurrentFlowStatusName}</Typography>}
            </>}</TableCell>
            <TableCell>{dateLabel(batch.hcpCurrentFlowStartedAt)}</TableCell>
            <TableCell>{dateLabel(batch.hcpCurrentFlowDeadline)}</TableCell>
            <TableCell><Chip label={batch.hcpFinished ? 'Finalizado' : 'Em aberto'} size="small" color={batch.hcpFinished ? 'success' : 'default'} variant="outlined" />
              {batch.hcpFinished && batch.finishedAt && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{dateLabel(batch.finishedAt)}</Typography>}
            </TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
  </Box>
}
